<template>
  <div>
    <h1
      :style="{marginTop: 0, marginBottom: '30px'}"
      data-automation="setup-title"
    >
      Setup
    </h1>
    <p>
      This page helps you obtain frozen URLs and has instructions on how to use those frozen URLs to install packages.
      For more information about this feature, please refer to the <a
        :href="quickStartURL"
        target="_blank"
      >Quick Start guide</a>.
    </p>
    <p>
      Before you begin, check to see if R is already configured to use this repository.  Run <code>options('repos')</code>
      in an R console to identify where R will look for packages. If this doesn't show a URL to your RSPM instance, like
      <code>{{ pmServerURL + currentRepo.name }}</code>, or if you'd like to change to a different snapshot, read on.
    </p>

    <UpsiDatePicker
      :style="{marginBottom: '50px'}"
      @repoURL="childRepoURL => { repoURL = childRepoURL }"
    />

    <div
      v-if="hasSysReqs"
    >
      <div
        class="actionBar inline"
      >
        <h2 style="display:inline">
          Install System Prerequisites
        </h2>
        <ActionButton
          :button-class="systemDependenciesInfoClass"
          label="info"
          data-automation="setup-sysreq-info"
          @doIt="toggleShowSystemDependenciesInfo"
        />
      </div>
      <div
        class="textWithButton"
        style="margin-bottom: 5px;"
      >
        <h3>
          <span style="font-size: .7em; padding-left: 5px;">
            Terminal commands to install <span
              class="setupCodeLabel"
              data-automation="setup-sysreq-os-label"
            >{{ selectedOS.display }} </span>(<a
              class="changeLink"
              data-automation="setup-binary-distro-change"
              href=""
              @click.prevent="handleChangeOSSystem"
            >change</a>) system dependencies for the packages in this repository. May require root privileges.
          </span>
        </h3>
        <div>
          <button
            v-if="haveInstructions && !gettingSystemDependencies"
            class="copyButton"
            data-clipboard-target="#sysInstallInstructions"
            data-automation="setup-sysreq-copy"
          >
            Copy
          </button>
        </div>
      </div>

      <div
        v-if="showSystemDependenciesInfo"
        class="toggleableInfo"
      >
        <p data-automation="setup-sysreq-info-text">
          Known system prerequisites are automatically detected from the package's
          system requirements information.
          See the <a
            data-automation="package-sysreqs-link"
            :href="sysreqsDocURL"
            target="_blank"
          >admin guide</a> for more
          details on system dependency detection and its limitations.
        </p>
      </div>

      <GettingMore
        :show="gettingSystemDependencies"
        label="Getting system prerequisites"
      />
      <pre
        v-if="haveInstructions && !gettingSystemDependencies"
        style="max-height: 165px;"
      ><code
        id="sysInstallInstructions"
        data-automation="setup-sysreq-instructions"
      >{{ instructions }}</code></pre>
      <div
        v-if="!haveInstructions && !gettingSystemDependencies"
        class="toggleableInfo"
      >
        <p>No known prerequisites</p>
      </div>
    </div>

    <h2> Using Packages Inside RStudio</h2>

    <p>
      In RStudio 1.2 or above, repository settings are configured through the
      <span class="emphasize">Global Options</span> menu available from the
      <span class="emphasize">Tools</span> section of the main toolbar. In the
      <span class="emphasize">Packages</span> submenu, copy the repository URL into the "Primary CRAN repo" field or click
      <span class="emphasize">Add</span> to include this repository as a secondary repo.
    </p>
    <div
      class="repoOptionsImage"
      data-automation="setup-repo-image"
    />

    <h2>
      Instructions for RStudio Server Admins
    </h2>
    <p>
      If your organization uses RStudio Server, an administrator can configure
      the repository options for all users in a configuration file. Instructions for
      specific scenarios are available in the <a
        :href="rstudioServerDocURL"
        target="_blank"
        data-automation="setup-admin-guide"
      >admin guide</a>.
    </p><h2> Outside of RStudio </h2>
    <p style="margin-bottom: 0px;">
      To configure R outside of RStudio, set the <a
        href="http://stat.ethz.ch/R-manual/R-devel/library/base/html/options.html"
        target="_blank"
        rel="noopener noreferrer"
        data-automation="setup-option"
      ><code>repos</code> option</a> in R to include the repository URL at the
      top of the page.
    </p>
    <div class="textWithButton">
      <h3>
        <span
          data-automation="configure-r-label"
          style="font-size: .7em; padding-left: 5px;"
        >Example repository setup code</span>
      </h3>
      <div>
        <button
          class="copyButton"
          data-clipboard-target="#optionsRepo"
          data-automation="configure-r-label"
        >
          Copy
        </button>
      </div>
    </div>
    <pre
      id="optionsRepo"
      data-automation="configure-r"
    ><span>options(repos = c(REPO_NAME = "{{ repoURL }}"))</span></pre>
    <p>
      We recommend adding this to your R startup file (<code>Rprofile.site</code>
      or <code>.Rprofile</code>) to maintain the configuration across R sessions.
      More information about managing R startup files is available in
      <a
        href="https://support.rstudio.com/hc/en-us/articles/360047157094-Managing-R-with-Rprofile-Renviron-Rprofile-site-Renviron-site-rsession-conf-and-repos-conf"
        target="_blank"
        rel="noopener noreferrer"
      >this article</a>.
    </p>

    <ChangeOSDialog
      v-if="showChangeOSSystemDialog"
      @closeDialog="closeChangeOSSystemDialog"
    />
  </div>
</template>

<script>

import { mapGetters, mapState, mapActions } from 'vuex'
import { serverPath } from '../../api/http-common'
import * as types from '../../store/mutation-types'
import GettingMore from '../GettingMore'
import ActionButton from '../ActionButton'
import UpsiDatePicker from '../UpsiDatePicker'
import ChangeOSDialog from '../dialogs/ChangeOSDialog'

function printSystemRequirement (result) {
  if (!result) {
    return ''
  }
  const script = []
  result.forEach(req => {
    script.push('# ' + req.name + ' requirements:')
    // Collapse all of the instructions.
    if (req.requirements.pre_install) {
      req.requirements.pre_install.forEach(cmd => {
        script.push(cmd.command)
        script.push(cmd.script)
      })
    }
    if (req.requirements.install_scripts) {
      req.requirements.install_scripts.forEach(install => {
        script.push(install)
      })
    }
    if (req.requirements.post_install) {
      req.requirements.post_install.forEach(cmd => {
        script.push(cmd.command)
        script.push(cmd.script)
      })
    }
    script.push('')
  })
  return script.filter(s => s != null).join('\n')
}

export default {
  name: 'OverviewR',
  components: {
    UpsiDatePicker,
    GettingMore,
    ActionButton,
    ChangeOSDialog,
  },

  data: function () {
    return {
      userRepoName: 'RSPM',
      showSystemDependenciesInfo: false,
      // We store this state in the child component. It emits an event when it changes.
      repoURL: '',
      showChangeOSSystemDialog: false,
    }
  },
  computed: {
    ...mapState({
      currentRepoId: state => state.repos.currentRepoId,
      currentRepoSources: state => state.repos.currentRepoSources,
      selectedOS: state => state.packages.selectedOS,
      gettingSystemDependencies: state => state.repos.gettingRepoSystemDependencies,
      instructions: state => printSystemRequirement(state.repos.currentRepoSystemDependencies),
      binaries_enabled: state => state.status.binaries_enabled,
    }),
    ...mapGetters([
      'currentRepo',
    ]),
    pmServerURL: function () {
      return serverPath()
    },
    quickStartURL: function () {
      return `${serverPath()}__docs__/admin/getting-started/user-interface-overview/#getting-frozen-urls-for-improving-reproducibility`
    },
    sysreqsDocURL: function () {
      return `${serverPath()}__docs__/admin/appendix/system-dependency-detection/`
    },
    rstudioServerDocURL: function () {
      return `${serverPath()}__docs__/admin/rstudio-server/`
    },
    hasSysReqs: function () {
      return this.selectedOS.sysReqs
    },
    binaryLinkDisabled: function () {
      return this.selectedOS !== null && this.selectedOS.binaryURL !== '' && !this.selectedOS.binaries
    },
    systemDependenciesInfoClass: function () {
      return this.showSystemDependenciesInfo ? 'toggleInfo current' : 'toggleInfo'
    },
    haveInstructions: function () {
      return this.instructions && this.instructions.length > 0
    },
  },
  watch: {
    currentRepoId: function (value) {
      this.getSystemDependencies()
    },
    selectedOS: function (newValue, oldValue) {
      this.getSystemDependencies()
    },
  },
  created: function () {
    this.getSystemDependencies()
  },
  mounted: function () {
    this.$store.commit(types.SET_CURRENT_NAV, 'overview')
  },
  methods: {
    ...mapActions([
      'getRepoSystemDependencies',
    ]),
    getSystemDependencies: function () {
      if (this.currentRepoId && this.selectedOS.sysReqs) {
        this.getRepoSystemDependencies({ repoId: this.currentRepoId, os: this.selectedOS })
      }
    },
    toggleShowSystemDependenciesInfo: function () {
      this.showSystemDependenciesInfo = !this.showSystemDependenciesInfo
    },
    // We have this for system dependencies. The binary dialog also has it, but that's under the UpsiDatePicker component.
    handleChangeOSSystem: function () {
      this.showChangeOSSystemDialog = true
    },
    closeChangeOSSystemDialog: function () {
      this.showChangeOSSystemDialog = false
      this.getSystemDependencies()
    },
  },
}

</script>

<style scoped>
  h4, .emphasize {
    font-weight: bold;
  }

  p, h4 {
    font-size: 16px;
  }
</style>
